import React from "react";
import SimpleTemplate from "@insightfulscience/atomic-react/SimpleTemplate";
import SimpleTemplateTitle from "@insightfulscience/atomic-react/SimpleTemplate/Title";
import SimpleTemplateDescription from "@insightfulscience/atomic-react/SimpleTemplate/Description";
import { idX, isLoaded } from "@insightfulscience/shared-utils/fn";
import WithConditions from "@insightfulscience/smart-react/hocs/WithConditions";
import { isExpectedGqlError } from "@insightfulscience/shared-utils/gqlClient/GqlError";
import { useLocale } from "@insightfulscience/smart-react/i18";
import pipe from "@insightfulscience/shared-utils/fn/pipe";
import useQueryParam from "@insightfulscience/smart-react/nav-hooks/useQueryParam";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import ConfigureSubscriptionForm from "../../ConfigureSubscriptionForm";
import PLAN_WITH_PRICES from "./plan-with-prices.graphql";
import routes from "../../routes";
import { isIsul, isPlanCodeValid } from "../../domain/plan";
import WithServices from "../../services";
import { savePriceItem } from "./savePriceItem";
import withErrorHandling from "../../AppError/withErrorHandling";
import SiteLayout from "../../SiteLayout";
import { isPurchaseGroupAllowed } from "../../domain/subscription";
import WithMaintenance from "../../Maintenance/WithMaintenance";

const ConfigureSubscriptionPage = () => {
	const planCode = useQueryParam("planCode");
	const [t] = useLocale(
		isIsul(planCode) ? "configure-subscription-isul" : "configure-subscription",
	);
	return (
		<SiteLayout isWrapper={false}>
			<PageMeta title={t("PAGE_TITLE")}>
				<PageMeta.Robots content={t("META_ROBOTS_CONTENT")} />
			</PageMeta>
			<SimpleTemplate utility={{ bgColor: "4" }} cellWidth={9}>
				<SimpleTemplateTitle>{t("TITLE")}</SimpleTemplateTitle>
				<SimpleTemplateDescription>{t("PROMPT")}</SimpleTemplateDescription>
				<ConfigureSubscriptionForm />
			</SimpleTemplate>
		</SiteLayout>
	);
};

ConfigureSubscriptionPage.preConditions = [
	// clear priceItem and billingInfo
	[
		(_, { storageService }) => {
			storageService.clearPurchaseData();
			return true;
		},
		idX,
	],
	[({ query }) => isPlanCodeValid(query.planCode), routes.external.howToBuy], // pre-condition
	[({ query }) => isPurchaseGroupAllowed(query.planCode), routes.external.howToBuy], // pre-condition
	[
		({ query }, { publicConfig }) => publicConfig.ENV !== "PRODUCTION" || isIsul(query.planCode), // forbidden legacy plans on production
		routes.external.howToBuy,
	],
];

ConfigureSubscriptionPage.getInitialProps = ({ query }, { gqlClient }) =>
	gqlClient(PLAN_WITH_PRICES, { planCode: query.planCode }).then(
		({ getPlanWithPrices }) => getPlanWithPrices,
	);

ConfigureSubscriptionPage.postConditions = [
	[isLoaded, routes.external.howToBuy],
	[({ prices }) => prices?.length > 0, routes.external.howToBuy],
	[({ prices }) => prices.length !== 1, savePriceItem],
];

ConfigureSubscriptionPage.onError = [
	[isExpectedGqlError(400, 404), routes.external.howToBuy], // if Plan is not found
];

export default pipe(
	ConfigureSubscriptionPage,
	WithConditions,
	withErrorHandling,
	WithServices,
	WithMaintenance({ level: "NEW_ECOMM" }),
);
